import { action, makeObservable, observable } from 'mobx';
import { Model, IModelAttributes, attribute, entity } from 'Models/Model';
import * as Models from 'Models/Entities';
import { stringNotEmpty } from 'Util/TypeGuards';
/* eslint-disable max-len */

export interface IAdditionalBookingSectionEntityAttributes extends IModelAttributes {
	name: string;
	information: string;
	disabled: boolean;
	order: number;
	colour?: string;
	iconName?: string;

	additionalBookingOptions: Array<
		Models.AdditionalBookingOptionEntity | Models.IAdditionalBookingOptionEntityAttributes
	>;
	ferriess: Array<Models.FerriesAdditionalBookingSections | Models.IFerriesAdditionalBookingSectionsAttributes>;
	routess: Array<Models.RoutesAdditionalBookingSections | Models.IRoutesAdditionalBookingSectionsAttributes>;
	maxAdditionalOptionsPerBooking?: number;
}
@entity('AdditionalBookingSectionEntity', 'Additional Booking Section')
export default class AdditionalBookingSectionEntity extends Model implements IAdditionalBookingSectionEntityAttributes {
	/**
	 * Fields to exclude from the JSON serialization in create operations.
	 */
	public static excludeFromCreate: string[] = [];

	/**
	 * Fields to exclude from the JSON serialization in update operations.
	 */
	public static excludeFromUpdate: string[] = [];

	@observable
	@attribute<AdditionalBookingSectionEntity, string>()
	public name: string;

	@observable
	@attribute<AdditionalBookingSectionEntity, string>()
	public information: string;

	@observable
	@attribute<AdditionalBookingSectionEntity, string>()
	public colour: string;

	@observable
	@attribute<AdditionalBookingSectionEntity, string>()
	public iconName: string;

	@observable
	@attribute<AdditionalBookingSectionEntity, boolean>()
	public disabled: boolean;

	@observable
	@attribute<AdditionalBookingSectionEntity, number>()
	public order: number;

	@observable
	@attribute({ isReference: true, manyReference: true })
	public additionalBookingOptions: Models.AdditionalBookingOptionEntity[] = [];

	@observable
	@attribute({ isReference: true, manyReference: true })
	public ferriess: Models.FerriesAdditionalBookingSections[] = [];

	@observable
	@attribute({ isReference: true, manyReference: true })
	public routess: Models.RoutesAdditionalBookingSections[] = [];

	/**
	 * Defines the maximum number of additional booking options that can be selected within a single booking.
	 *
	 * If this value is `null` or `undefined`, there is no limit on the number of additional options
	 * that can be selected.
	 *
	 * This limit applies to the combined total of all additional options within a section, ensuring
	 * users cannot exceed the predefined maximum. Once this limit is reached, further selections
	 * or increases in option counts will be restricted.
	 *
	 * Example:
	 * If `maxAdditionalOptionsPerBooking` is set to `5`, users can select up to 5 additional options in total,
	 * regardless of how they are distributed among different options.
	 */
	@observable
	@attribute<AdditionalBookingSectionEntity, number>()
	public maxAdditionalOptionsPerBooking?: number;

	constructor(attributes?: Partial<IAdditionalBookingSectionEntityAttributes>) {
		super(attributes);

		this.additionalBookingOptions = [];
		this.ferriess = [];
		this.routess = [];

		makeObservable(this);

		this.assignAttributes(attributes);
	}

	/**
	 * Assigns fields from a passed in JSON object to the fields in this model.
	 * Any reference objects that are passed in are converted to models if they are not already.
	 * This function is called from the constructor to assign the initial fields.
	 */
	public assignAttributes(attributes?: Partial<IAdditionalBookingSectionEntityAttributes>) {
		super.assignAttributes(attributes);

		if (attributes) {
			if (attributes.name !== undefined) {
				this.name = attributes.name;
			}
			if (attributes.information !== undefined) {
				this.information = attributes.information;
			}
			if (attributes.disabled !== undefined) {
				this.disabled = attributes.disabled;
			}
			if (attributes.order !== undefined) {
				this.order = attributes.order;
			}
			if (stringNotEmpty(attributes.colour)) {
				this.colour = attributes.colour;
			}
			if (stringNotEmpty(attributes.iconName)) {
				this.iconName = attributes.iconName;
			}
			if (
				attributes.additionalBookingOptions !== undefined &&
				Array.isArray(attributes.additionalBookingOptions)
			) {
				if (!this.additionalBookingOptions) {
					this.additionalBookingOptions = [];
				}
				for (const model of attributes.additionalBookingOptions) {
					if (model instanceof Models.AdditionalBookingOptionEntity) {
						this.additionalBookingOptions.push(model);
					} else {
						this.additionalBookingOptions.push(new Models.AdditionalBookingOptionEntity(model));
					}
				}
			}
			if (attributes.ferriess !== undefined && Array.isArray(attributes.ferriess)) {
				if (!this.ferriess) {
					this.ferriess = [];
				}
				for (const model of attributes.ferriess) {
					if (model instanceof Models.FerriesAdditionalBookingSections) {
						this.ferriess.push(model);
					} else {
						this.ferriess.push(new Models.FerriesAdditionalBookingSections(model));
					}
				}
			}
			if (attributes.routess !== undefined && Array.isArray(attributes.routess)) {
				if (!this.routess) {
					this.routess = [];
				}
				for (const model of attributes.routess) {
					if (model instanceof Models.RoutesAdditionalBookingSections) {
						this.routess.push(model);
					} else {
						this.routess.push(new Models.RoutesAdditionalBookingSections(model));
					}
				}
			}
			if (attributes.order !== undefined) {
				this.order = attributes.order;
			}
			if (attributes.maxAdditionalOptionsPerBooking !== undefined) {
				this.maxAdditionalOptionsPerBooking = attributes.maxAdditionalOptionsPerBooking;
			}
		}
	}
}
